// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
// Import registerUser from the service
import { loginUser as apiLogin, 
  registerUser as apiRegister, 
  verifyToken as apiVerifyToken,
  logoutUser as apiLogout,
  refreshToken as apiRefreshToken
} from '../services/authService';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = useCallback(async () => {
    setIsLoading(true); // Ensure loading is true at the start of the check
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        // You could optionally verify the token with the backend here for added security on app load.
        // For instance, if your apiVerifyToken sets isAuthenticated:
        // await apiVerifyToken(storedToken); 
        // setIsAuthenticated(true); 
        // setToken(storedToken);
        // However, for simplicity, if a token exists, we assume it's valid until an API call fails.
        // This matches your current logic.
        setToken(storedToken);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Token verification failed on load:', error);
        localStorage.removeItem('token');
        setToken(null);
        setIsAuthenticated(false);
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (username, password) => {
    const data = await apiLogin(username, password); // Calls your service
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setIsAuthenticated(true);
    // No need to return data, the state update will trigger re-renders.
    // The Register.js handleSubmit will proceed after this await.
  };

  const register = async (username, email, password) => {
    // This function will call the registration API.
    // It does NOT set the auth state (token, isAuthenticated).
    // The Register.js component will call the login function separately after this.
    try {
      const data = await apiRegister(username, email, password); // Calls your new service
      return data; // Optionally return data (e.g., created user object or success message)
    } catch (error) {
      // Re-throw the error so the component can catch it and display it
      throw error;
    }
  };

  const logout = useCallback(async () => {
    try {
      // Call the backend to clear the httpOnly cookie
      await apiLogout();
    } catch (error) {
      // Even if the backend call fails (e.g., server is down),
      // we should still clear the client-side state.
      console.error("Logout API call failed, but logging out client-side anyway.", error);
    } finally {
      // Clear client-side token and state
      localStorage.removeItem('token');
      setToken(null);
      setIsAuthenticated(false);
    }
  }, []);

  const refresh = async () => {
    try {
      const data = await apiRefreshToken();
      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      setIsAuthenticated(true);
      return data.access_token; // Return the new token
    } catch (error) {
      console.error('Token refresh failed, logging out.', error);
      await logout(); // Trigger a full logout
      throw error; // Re-throw the error so the calling function knows it failed
    }
  };

  return (
    <AuthContext.Provider value={{ 
      token, 
      isAuthenticated, 
      isLoading, 
      login, 
      logout, 
      register, 
      refresh, // <-- Expose the new refresh function
      checkAuthStatus 
    }}>
      {children}
    </AuthContext.Provider>
  );
};